/**
 * Commit Validation
 * Validates that a player's activity choice is legal given the current state.
 * Returns null if valid, or an error string if invalid.
 */

import { findPath, tileDistance } from '@omen-quest/grid-map';

import type { StretchState, StretchActivity } from './types';
import { DEFAULT_MOVEMENT_BUDGET, STEALTH_SPEED_MULTIPLIER } from './types';

/** Validate a commit. Returns null if valid, error message if not. */
export function validateCommit(
  state: StretchState,
  playerId: string,
  activity: StretchActivity,
): string | null {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return 'Player not found';
  if (player.hasCommitted) return 'Player already committed';
  if (state.phase !== 'planning') return 'Not in planning phase';

  switch (activity.type) {
    case 'move':
      return validateMove(state, player, activity.path);

    case 'explore':
      if (activity.path) {
        const halfBudget = Math.floor(player.movementBudget * 0.5);
        return validatePath(state, player, activity.path, halfBudget);
      }
      return null;

    case 'move_quietly':
      if (activity.path) {
        const halfBudget = Math.floor(player.movementBudget * STEALTH_SPEED_MULTIPLIER);
        return validatePath(state, player, activity.path, halfBudget);
      }
      return null;

    case 'interact':
      return validateInteract(state, player, activity.x, activity.y);

    case 'help':
      return validateHelp(state, playerId, activity.targetPlayerId);

    case 'search':
    case 'keep_watch':
    case 'loot':
    case 'cast_spell':
    case 'take_your_time':
    case 'wait':
      return null;

    default:
      return 'Unknown activity type';
  }
}

/** Validate a movement path is within budget and walkable. */
function validateMove(
  state: StretchState,
  player: { x: number; y: number; movementBudget: number },
  path: Array<{ x: number; y: number }>,
): string | null {
  return validatePath(state, player, path, player.movementBudget);
}

/** Validate a path against a given budget. */
function validatePath(
  state: StretchState,
  player: { x: number; y: number },
  path: Array<{ x: number; y: number }>,
  budget: number,
): string | null {
  if (path.length === 0) return 'Path is empty';
  if (path.length > budget + 1) return 'Path exceeds movement budget';

  // First step must be adjacent to player (or player's position)
  const first = path[0];
  if (tileDistance(player.x, player.y, first.x, first.y) > 1) {
    return 'Path does not start adjacent to player';
  }

  // Verify path is contiguous (each step adjacent to previous)
  for (let i = 1; i < path.length; i++) {
    if (tileDistance(path[i - 1].x, path[i - 1].y, path[i].x, path[i].y) > 1) {
      return 'Path is not contiguous';
    }
  }

  // Verify destination is reachable via pathfinding
  const dest = path[path.length - 1];
  const validPath = findPath(
    state.map,
    { x: player.x, y: player.y },
    { x: dest.x, y: dest.y },
    state.openDoors,
  );
  if (validPath.length === 0) return 'Destination is unreachable';

  // The pathfinding result includes the start tile, so path cost = length - 1
  const pathCost = validPath.length - 1;
  if (pathCost > budget) return 'Shortest path to destination exceeds budget';

  return null;
}

/** Validate interact target is within reach (adjacent or on same tile). */
function validateInteract(
  state: StretchState,
  player: { x: number; y: number },
  targetX: number,
  targetY: number,
): string | null {
  const dist = tileDistance(player.x, player.y, targetX, targetY);
  if (dist > 1) return 'Interact target is too far away';
  return null;
}

/** Validate help target exists and is not self. */
function validateHelp(
  state: StretchState,
  playerId: string,
  targetPlayerId: string,
): string | null {
  if (playerId === targetPlayerId) return 'Cannot help yourself';
  const target = state.players.find(p => p.id === targetPlayerId);
  if (!target) return 'Help target not found';
  return null;
}
